import { useEffect } from "react";

/**
 * Open the dial pad from the keyboard.
 *
 * Its own file rather than another effect in `CallProvider`, which is at the
 * §4 ceiling: this is a listener with one condition and nothing to do with the
 * device, the queue or the call state machine that file exists to hold.
 *
 * ⚠ THE CHORD IS NOT GUARANTEED, and that is stated here rather than discovered
 * on somebody's laptop. Cmd/Ctrl+Shift+D is Chrome's "bookmark all tabs".
 * `preventDefault` takes it from the browser the same way web apps take Cmd+D,
 * but a browser or an extension is free to keep it, and there is no way to ask
 * from here whether it did. So this is the CONVENIENT door and not the only
 * one: the command palette (Cmd+K) offers a number it can dial, and that path
 * costs the browser nothing to allow.
 *
 * Deaf while a call is up. The window is already on screen showing the call, so
 * the only thing the press would do is leave `dialerOpen` set - and the pad
 * would then appear by itself once the finished call is dismissed, which reads
 * as the app opening a dialog nobody asked for a minute after they asked for it.
 */
export function useDialShortcut(open: () => void, enabled: boolean, idle: boolean): void {
  useEffect(() => {
    if (!enabled || !idle) return;
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || !e.shiftKey) return;
      // `code` rather than `key`: with Shift held, and on a layout that puts
      // something else on that key, `key` is not reliably "d" - while the
      // physical key under the operator's finger is what the chord is about.
      if (e.code !== "KeyD") return;
      e.preventDefault();
      open();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, enabled, idle]);
}
